import { useSpring, animated } from '@react-spring/web';
import { useMentiSpring } from '@mentimeter/motion/spring';
import {
  getRectanglePath,
  getStartingAnimationPath,
  type AnimationDirection,
} from './RectanglePath';

export type RectangleBackground = { image: string } | { color: string };

function hasImage(obj: RectangleBackground): obj is { image: string } {
  return 'image' in obj;
}

export interface RectangleWithCustomRadiusProps {
  background: RectangleBackground;
  x: number;
  y: number;
  width: number;
  height: number;
  topLeftRadius?: number;
  topRightRadius?: number;
  bottomLeftRadius?: number;
  bottomRightRadius?: number;
  animateFromY?: number | undefined;
  animationDirection: AnimationDirection;
  opacity?: number;
  disableAnimations?: boolean;
}

export const RectangleWithCustomRadius = ({
  background,
  x,
  y,
  width,
  height,
  topLeftRadius = 0,
  topRightRadius = 0,
  bottomLeftRadius = 0,
  bottomRightRadius = 0,
  animateFromY,
  animationDirection,
  opacity = 1,
  disableAnimations = false,
  ...rest
}: RectangleWithCustomRadiusProps) => {
  const { opacity: animatedOpacity } = useSpring({
    opacity,
  });

  const [styles] = useMentiSpring<{
    x: number;
    y: number;
    width: number;
    path: string;
  }>({
    from: {
      y:
        animationDirection.type === 'vertical' && animationDirection.fromY
          ? (animationDirection.fromY ?? y)
          : y,
      path: getStartingAnimationPath({
        x,
        y,
        width,
        height,
        topLeftRadius,
        topRightRadius,
        bottomLeftRadius,
        bottomRightRadius,
        animationDirection,
      }),
    },
    to: {
      y,
      path: getRectanglePath({
        x,
        y,
        width,
        height,
        topLeftRadius,
        topRightRadius,
        bottomLeftRadius,
        bottomRightRadius,
      }),
    },
    immediate: disableAnimations,
  });

  if (hasImage(background)) {
    return (
      <g {...rest}>
        <defs>
          <clipPath id={`image-${x}`}>
            <animated.path d={styles.path} opacity={animatedOpacity} />
          </clipPath>
        </defs>
        <animated.image
          href={background.image}
          x={x}
          y={styles.y}
          width={width}
          height={width}
          imageRendering="optimizeSpeed"
          clipPath={`url(#${`image-${x}`})`}
          opacity={animatedOpacity}
        />
      </g>
    );
  }

  return (
    <g {...rest}>
      <animated.path
        d={styles.path}
        fill={background.color}
        opacity={animatedOpacity}
      />
    </g>
  );
};
